
import { Link, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

export function Footer() {
  const navigate = useNavigate();

  /**
   * Scrolls smoothly to an element with the given id.
   * If we are not on the home page, it first navigates there,
   * then performs the scroll after a short delay.
   *
   * @param id - The id of the target element to scroll to.
   */
  const scrollToSection = useCallback(
    (id: string) => {
      if (!id) return;

      const performScroll = () => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          console.warn(`Footer: No element found with id "${id}"`);
        }
      };

      // If we are already on the home page, scroll immediately.
      if (window.location.pathname === '/') {
        performScroll();
        return;
      }

      // Otherwise navigate to home first, then scroll.
      navigate('/');
      // Give the new page a moment to render before scrolling.
      const timeout = setTimeout(performScroll, 500);

      // Clean‑up in case the component unmounts before the timeout fires.
      return () => clearTimeout(timeout);
    },
    [navigate]
  );

  return (
    <footer id="contact" className="bg-[#505A5A] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-1">
            <img
              src="https://static.readdy.ai/image/55893c4a5915be14917a0a81b6ec721d/f5868736a4ed9c94ff4146ae0bef5b91.png"
              alt="Furnished Rental Management Canada Logo"
              className="h-24 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-base text-gray-300 leading-relaxed">
              Compliant furnished rental and short-stay management across Metro
              Vancouver
            </p>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-4">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/compliance-review"
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
                >
                  Compliance Review
                </Link>
              </li>
              <li>
                <Link
                  to="/str-cohosting"
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
                >
                  Short-Term Co-Hosting
                </Link>
              </li>
              <li>
                <Link
                  to="/medium-term-rentals"
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
                >
                  Medium-Term Rentals
                </Link>
              </li>
              <li>
                <Link
                  to="/listing-setup"
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
                >
                  Listing Setup
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-4">Tools</h4>
            <ul className="space-y-3">
              <li>
                <button
                  type="button"
                  onClick={() => scrollToSection('cities')}
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0"
                >
                  Cities
                </button>
              </li>
              <li>
                <Link
                  to="/buildings-that-allow-str"
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
                >
                  STR Buildings
                </Link>
              </li>
              <li>
                <Link
                  to="/revenue-calculator"
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
                >
                  Revenue Calculator
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => scrollToSection('eligibility-check')}
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0"
                >
                  Eligibility Check
                </button>
              </li>
              <li>
                <Link
                  to="/faq"
                  className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer"
                >
                  FAQs
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-3">Have a question?</h4>
            <p className="text-sm text-gray-300 mb-4">
              Reach out anytime. We&apos;re here to help you understand your
              options.
            </p>
            <a
              href="mailto:contact@furnishedrentalmanagement.com"
              className="inline-block border-2 border-[#882629] text-[#882629] bg-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#882629] hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              Contact Us
            </a>
          </div>
        </div>

        <div className="border-t border-gray-400 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-300">
              © 2025 Compliant Rental Management. All rights reserved.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-300 hover:text-white transition-colors"
            >
              Website Builder
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 text-center pb-4 opacity-10 pointer-events-none">
        <span className="text-8xl font-bold text-white font-['Fraunces']">
          COMPLIANT
        </span>
      </div>
    </footer>
  );
}
